import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {observer} from 'mobx-react-lite';

import {useProductDisplayInfo} from '../../../hooks/useProductDisplayInfo';
import {AppRoute} from '../../../models/AppRoute';
import useAppNavigation from '../../../navigation/useAppNavigation';
import {renderSticker} from '../../../utils/functions';
import {buildProductTestId} from '../../../utils/string';
import MembersPriceLabel from '../primitives/MembersPriceLabel';
import ProductTileActions from '../primitives/ProductTileActions';
import ProductTileImage from '../primitives/ProductTileImage';
import ProductTileInfo from '../primitives/ProductTileInfo';
import ProductTilePricing from '../primitives/ProductTilePricing';
import {TILE_DIMENSIONS, TYPOGRAPHY} from '../styles';
import {ProductTileVariantProps} from '../types';

/**
 * Modal-safe product tile variant using a simple vertical stack layout.
 *
 * Unlike ProductTileCarousel which uses flex:1 + space-between (requiring a
 * resolved parent height), this variant uses content-driven height with no
 * overflow:hidden. This makes it safe for use inside horizontal ScrollViews
 * and FlatLists within modals, where the native layout engine cannot resolve
 * cross-axis height for flex-based children.
 */
const ProductTileModal = observer(function ProductTileModal({
  item,
  onPress,
  testContext,
  disabled = false,
  containerStyle,
  isProcessing = false,
}: ProductTileVariantProps & {isProcessing?: boolean}) {
  const navigation = useAppNavigation();
  const testID = buildProductTestId(item.name, testContext);
  const {externalSellType, productDisplayRules} = useProductDisplayInfo(item);
  const hasStickers = item.stickers && item.stickers.length > 0;

  const handlePress = () => {
    if (isProcessing) return;

    if (onPress) {
      onPress(item);
    } else {
      // @ts-expect-error - Navigation type is complex with nested screens
      navigation.navigate(AppRoute.ROOT, {
        screen: AppRoute.MAIN,
        params: {
          screen: AppRoute.PRODUCT,
          params: {product: item},
        },
      });
    }
  };

  return (
    <Pressable
      style={({pressed}) => [styles.container, containerStyle, pressed && styles.pressed]}
      onPress={handlePress}
      disabled={disabled || isProcessing || !onPress}
      testID={testID}>
      <MembersPriceLabel
        externalSellType={externalSellType}
        productDisplayRules={productDisplayRules}
        center
      />

      <View style={styles.imageContainer}>
        <ProductTileImage item={item} size="medium" testID={`${testID}-image`} />
        {hasStickers ? (
          <View style={styles.stickerContainer}>
            {item.stickers?.map(sticker => renderSticker(sticker))}
          </View>
        ) : null}
      </View>

      <ProductTileInfo
        item={item}
        nameNumberOfLines={2}
        testID={`${testID}-name`}
        nameStyle={styles.name}
      />

      <ProductTilePricing
        item={item}
        showMemberLabel={false}
        showOriginalPrice
        testID={`${testID}-price`}
      />

      <ProductTileActions
        item={item}
        testContext={testContext}
        testID={`${testID}-actions`}
        containerStyle={styles.actionsContainer}
      />
    </Pressable>
  );
});

export default ProductTileModal;

const styles = StyleSheet.create({
  // Simple vertical stack — no overflow:hidden, no flex:1.
  // Content drives height, safe for horizontal scroll in modals on native.
  container: {
    width: TILE_DIMENSIONS.width,
    padding: TILE_DIMENSIONS.containerPadding,
    backgroundColor: 'white',
    borderRadius: TILE_DIMENSIONS.borderRadius,
    borderWidth: TILE_DIMENSIONS.borderWidth,
    borderColor: '#CCC',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: TILE_DIMENSIONS.shadowOffsetHeight},
    shadowOpacity: TILE_DIMENSIONS.shadowOpacity,
    shadowRadius: TILE_DIMENSIONS.shadowRadius,
    elevation: TILE_DIMENSIONS.elevation,
  },
  pressed: {
    opacity: 0.7,
  },
  imageContainer: {
    position: 'relative',
    alignItems: 'center',
    marginTop: TILE_DIMENSIONS.imageMarginTop,
    marginBottom: TILE_DIMENSIONS.imageMarginBottom,
  },
  stickerContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,
    pointerEvents: 'none',
  },
  name: {
    ...TYPOGRAPHY.name,
    height: TILE_DIMENSIONS.nameFixedHeight,
    marginBottom: TILE_DIMENSIONS.infoMarginBottom,
  },
  actionsContainer: {
    width: '100%',
    alignItems: 'stretch',
    marginTop: TILE_DIMENSIONS.pricingMarginBottom,
  },
});
